import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { EsBillingDetails, EsBillingPeriod } from '@/types/earlySupporter';

/**
 * Client-side draft of the Early Supporter upgrade flow. Kept in sessionStorage
 * so "try again" from the return page lands on the review step with everything
 * still filled in. Never holds payment data (payment happens on the shop page).
 */

export const EMPTY_BILLING: EsBillingDetails = {
  firstName: '',
  lastName: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  postcode: '',
  country: '',
  vatId: '',
};

/** sessionStorage key of the persisted draft (also cleared on logout). */
export const ES_FLOW_STORAGE_KEY = 'aa-es-flow';

interface EsFlowState {
  /** Owner of the draft; a different signed-in user discards it. */
  userId: number | null;
  packageId: number | null;
  period: EsBillingPeriod;
  billing: EsBillingDetails;
  couponCode: string;
  bindUser: (userId: number | null) => void;
  setPackageId: (id: number | null) => void;
  setPeriod: (period: EsBillingPeriod) => void;
  setBilling: (patch: Partial<EsBillingDetails>) => void;
  setCouponCode: (code: string) => void;
  reset: () => void;
}

export const useEarlySupporterStore = create<EsFlowState>()(
  persist(
    (set, get) => ({
      userId: null,
      packageId: null,
      period: 'YEAR',
      billing: EMPTY_BILLING,
      couponCode: '',
      bindUser: (userId) => {
        if (get().userId === userId) return;
        set({ userId, packageId: null, period: 'YEAR', billing: EMPTY_BILLING, couponCode: '' });
      },
      setPackageId: (packageId) => set({ packageId }),
      setPeriod: (period) => set({ period }),
      setBilling: (patch) => set((s) => ({ billing: { ...s.billing, ...patch } })),
      setCouponCode: (couponCode) => set({ couponCode }),
      reset: () => set({ userId: get().userId, packageId: null, period: 'YEAR', billing: EMPTY_BILLING, couponCode: '' }),
    }),
    {
      name: ES_FLOW_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ userId: s.userId, packageId: s.packageId, period: s.period, billing: s.billing, couponCode: s.couponCode }),
    },
  ),
);
